import { useCallback, useRef, useState } from "react";

import type { BoundaryConditions } from "../../table-menu-plugin";

const minWidth = 1; // if the padding is too large it will still allow resizing smaller than the padding.

export function useColumnResizer({
  boundaryConditions,
  handleColumnWidthsChange,
  handleResizeEnd,
}: {
  boundaryConditions: BoundaryConditions | null;
  handleColumnWidthsChange: (
    newColumnWidths: number[],
    movingPillarIndex: number,
    movingHandleIndex: number,
  ) => void;
  handleResizeEnd: () => void;
}): {
  currentlyInteractingPillarIndex: null | number;
  handlePointerDown: (
    event: React.PointerEvent<HTMLDivElement>,
    movingPillarIndex: number, //left hand side of table row is -1th.
    startColumnWidths: readonly number[],
    movingHandleIndex: number,
  ) => void;
} {
  const [currentlyInteractingPillarIndex, setCurrentlyInteractingPillarIndex] = useState<
    null | number
  >(null);

  const startPointerLeftRef = useRef<number | null>(null);
  const currentlyInteractingPillarIndexRef = useRef<null | number>(null);
  const currentlyInteractingHandleIndexRef = useRef<null | number>(null);
  const startColumnWidthsRef = useRef<readonly number[]>([]);

  const handlePointerMove = useCallback(
    (event: PointerEvent): void => {
      if (
        !boundaryConditions ||
        startPointerLeftRef.current === null ||
        currentlyInteractingPillarIndexRef.current === null ||
        currentlyInteractingHandleIndexRef.current === null
      ) {
        return;
      }

      const { tableLeft, tableWidth } = boundaryConditions;

      const startPointerPercentage = (100 * (startPointerLeftRef.current - tableLeft)) / tableWidth;

      const nextPointerPixelsFromTableLeft = event.clientX - tableLeft;

      const nextPointerPercentage = 100 * (nextPointerPixelsFromTableLeft / tableWidth);

      const nextPointerChangeInPercentage = nextPointerPercentage - startPointerPercentage;

      // There will be a column to the left and right of the currently interacting pillar index
      const leftColumnWidthPercentage =
        startColumnWidthsRef.current[currentlyInteractingPillarIndexRef.current]!;
      const rightColumnWidthPercentage =
        startColumnWidthsRef.current[currentlyInteractingPillarIndexRef.current + 1]!;

      const newLeftColumnWidthPercentage =
        leftColumnWidthPercentage + nextPointerChangeInPercentage;
      const newRightColumnWidthPercentage =
        rightColumnWidthPercentage - nextPointerChangeInPercentage;

      const newColumnWidths = [...startColumnWidthsRef.current];
      // Check if the new widths are within the min and max bounds
      const maxColumnPairWidth =
        newLeftColumnWidthPercentage + newRightColumnWidthPercentage - minWidth;
      if (
        newLeftColumnWidthPercentage < minWidth ||
        newRightColumnWidthPercentage < minWidth ||
        newLeftColumnWidthPercentage > maxColumnPairWidth ||
        newRightColumnWidthPercentage > maxColumnPairWidth
      ) {
        // Stop resizing if either column gets too small
        return;
      }
      newColumnWidths[currentlyInteractingPillarIndexRef.current] = newLeftColumnWidthPercentage;
      newColumnWidths[currentlyInteractingPillarIndexRef.current + 1] =
        newRightColumnWidthPercentage;
      // Update the widths of the columns
      handleColumnWidthsChange(
        newColumnWidths,
        currentlyInteractingPillarIndexRef.current,
        currentlyInteractingHandleIndexRef.current,
      );
    },

    [boundaryConditions, handleColumnWidthsChange],
  );

  const handlePointerUp = useCallback((): void => {
    setCurrentlyInteractingPillarIndex(null);
    if (document.body !== null) {
      document.body.style.setProperty("cursor", "default");
      document.body.style.setProperty("user-select", "");
    }

    document.removeEventListener("pointermove", handlePointerMove);
    // Self-recursive useCallback; restructuring this needs its own change.
    // oxlint-disable-next-line react/immutability
    document.removeEventListener("pointerup", handlePointerUp);
    handleResizeEnd();
  }, [handlePointerMove, handleResizeEnd]);

  const handlePointerDown = useCallback(
    (
      event: React.PointerEvent<HTMLDivElement>,
      movingPillarIndex: number, //First coloumn joint is 0th
      startColumnWidths: readonly number[],
      movingHandleIndex: number,
    ): void => {
      // Bondary conditions means that a cell is currently selected
      if (!boundaryConditions) {
        return;
      }
      // Shouldn't happen but just in case, control the error.
      if (movingPillarIndex < 0) {
        console.error("Invalid movingPillarIndex:", movingPillarIndex);
        return;
      }
      if (movingPillarIndex > startColumnWidths.length - 2) {
        console.error("Invalid movingPillarIndex:", movingPillarIndex);
        return;
      }

      setCurrentlyInteractingPillarIndex(movingPillarIndex);
      currentlyInteractingPillarIndexRef.current = movingPillarIndex;
      currentlyInteractingHandleIndexRef.current = movingHandleIndex;
      startPointerLeftRef.current = event.clientX;
      startColumnWidthsRef.current = startColumnWidths;
      if (document.body !== null) {
        document.body.style.setProperty("cursor", `ew-resize`, "important");
        document.body.style.setProperty("user-select", `none`, "important");
      }
      document.addEventListener("pointermove", handlePointerMove);
      document.addEventListener("pointerup", handlePointerUp);
    },
    [handlePointerUp, handlePointerMove, setCurrentlyInteractingPillarIndex, boundaryConditions],
  );

  return {
    currentlyInteractingPillarIndex,
    handlePointerDown,
  };
}
